/**
 * BranchTeamPanel — drop-in "My Team" widget for Factory & Showroom dashboards.
 * Pulls /api/hr/branch-view and lets local managers:
 *   • see today's attendance + shift + check-in for every team member
 *   • mark attendance / OT / leave from the same widget (no page hop)
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Users, CalendarCheck, Clock, Timer, PlaneTakeoff, FileWarning, CheckCircle2 } from "lucide-react";
import { fmtDate, inr } from "@/lib/utils-format";

const workStatusTone = {
  active: "bg-emerald-100 text-emerald-800",
  on_leave: "bg-amber-100 text-amber-800",
  probation: "bg-blue-100 text-blue-800",
  notice_period: "bg-purple-100 text-purple-800",
  suspended: "bg-red-100 text-red-800",
  resigned: "bg-stone-100 text-stone-600",
};
const attTone = {
  present: "bg-emerald-100 text-emerald-800",
  absent: "bg-red-100 text-red-800",
  half_day: "bg-amber-100 text-amber-800",
  leave: "bg-blue-100 text-blue-800",
};

export default function BranchTeamPanel({ branchId, branchName }) {
  const [data, setData] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [attOpen, setAttOpen] = useState(false);
  const [otOpen, setOtOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [attForm, setAttForm] = useState({ employee_id: "", date: today, status: "present", check_in: "", check_out: "", shift_id: "" });
  const [otForm, setOtForm] = useState({ employee_id: "", date: today, hours: 1, rate_multiplier: 1.5, reason: "" });
  const [leaveForm, setLeaveForm] = useState({ employee_id: "", leave_type: "casual", from_date: today, to_date: today, reason: "" });

  const load = () => {
    if (!branchId) return;
    api.get("/hr/branch-view", { params: { branch_id: branchId } })
      .then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (!branchId) return;
    load();
    api.get("/hr/shifts", { params: { branch_id: branchId } }).then((r) => setShifts(r.data)).catch(() => {});
  }, [branchId]);

  const openAttFor = (emp) => { setAttForm({ ...attForm, employee_id: emp.id, shift_id: emp.default_shift_id || "" }); setAttOpen(true); };
  const openOtFor = (emp) => { setOtForm({ ...otForm, employee_id: emp.id }); setOtOpen(true); };
  const openLeaveFor = (emp) => { setLeaveForm({ ...leaveForm, employee_id: emp.id }); setLeaveOpen(true); };

  const saveAtt = async () => {
    try {
      await api.post("/hr/attendance", { ...attForm, branch_id: branchId, shift_id: attForm.shift_id || null });
      toast.success("Marked"); setAttOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveOt = async () => {
    try {
      await api.post("/hr/overtime", { ...otForm, hours: Number(otForm.hours), rate_multiplier: Number(otForm.rate_multiplier), branch_id: branchId });
      toast.success("OT recorded"); setOtOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveLeave = async () => {
    try {
      await api.post("/hr/leaves", leaveForm);
      toast.success("Leave logged"); setLeaveOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) {
    return (
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 text-center text-[#8B7355]" data-testid="branch-team-empty">
        Loading team…
      </div>
    );
  }
  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="branch-team-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="uc-label">Team</div>
          <h2 className="font-[Outfit] text-2xl font-semibold text-[#2C1810] flex items-center gap-2">
            <Users className="w-6 h-6 text-[#C05621]" /> My Team {branchName ? <span className="text-base text-[#8B7355] font-normal">· {branchName}</span> : null}
          </h2>
        </div>
        <div className="text-xs text-[#8B7355]">
          Staff assignment is managed centrally by HR. This view is read-only for team composition — but you can mark daily <b>Attendance / OT / Leave</b> from here.
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="branch-team-kpis">
        <Kpi label="Team Size" value={t.team_size} />
        <Kpi label="Primary" value={t.primary} tone="orange" />
        <Kpi label="Secondary" value={t.secondary} tone="purple" />
        <Kpi label="Present" value={t.present} tone="green" />
        <Kpi label="Absent" value={t.absent} tone="red" />
        <Kpi label="On Leave" value={t.on_leave} tone="amber" />
        <Kpi label="Unmarked" value={t.unmarked} tone={t.unmarked > 0 ? "amber" : "slate"} />
        <Kpi label="OT (month)" value={`${t.ot_hours_month}h / ₹${inr(t.ot_amount_month)}`} tone="purple" />
      </div>

      {/* Alerts */}
      {(data.pending_leaves.length > 0 || data.docs_expiring.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {data.pending_leaves.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="branch-team-pending-leaves">
              <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1"><PlaneTakeoff className="w-4 h-4" /> Pending Leaves ({data.pending_leaves.length})</div>
              <div className="text-xs space-y-1">
                {data.pending_leaves.slice(0, 5).map((l) => (
                  <div key={l.id}>· {l.employee_name} — {l.leave_type} · {fmtDate(l.from_date)} → {fmtDate(l.to_date)} ({l.days || "?"}d)</div>
                ))}
              </div>
            </div>
          )}
          {data.docs_expiring.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="branch-team-expiring-docs">
              <div className="flex items-center gap-2 text-red-800 font-semibold text-sm mb-1"><FileWarning className="w-4 h-4" /> Docs Expiring (30d) — {data.docs_expiring.length}</div>
              <div className="text-xs space-y-1">
                {data.docs_expiring.slice(0, 5).map((d) => (
                  <div key={d.id}>· {d.employee_name} — {(d.doc_type || "").replace(/_/g, " ")} on {fmtDate(d.expiry_date)}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Team table */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Dept · Designation</th>
              <th className="text-left px-3 py-2">Line</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Today</th>
              <th className="text-left px-3 py-2">Shift · Check-in</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="branch-team-rows">
            {data.team.map((e) => (
              <tr key={e.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 font-medium">
                  {e.name}
                  {e.is_secondary && <Badge className="ml-1 bg-purple-100 text-purple-800 border-0 text-[10px]">2°</Badge>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{e.employee_code}</td>
                <td className="px-3 py-2 text-xs"><span className="capitalize">{e.department}</span>{e.designation ? <span className="text-[#8B7355]"> · {e.designation}</span> : null}</td>
                <td className="px-3 py-2 text-xs">{e.production_line || <span className="text-[#8B7355]">—</span>}</td>
                <td className="px-3 py-2"><Badge className={`border-0 ${workStatusTone[e.work_status] || "bg-stone-100 text-stone-700"} capitalize`}>{(e.work_status || "").replace("_", " ")}</Badge></td>
                <td className="px-3 py-2">
                  {e.today_status ? <Badge className={`border-0 capitalize ${attTone[e.today_status] || "bg-stone-100 text-stone-700"}`}>{e.today_status.replace("_", " ")}</Badge> : <span className="text-[#8B7355] italic text-xs">not marked</span>}
                </td>
                <td className="px-3 py-2 text-xs">{e.today_shift || "—"}{e.today_check_in ? <span className="text-[#8B7355]"> · {e.today_check_in}</span> : null}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button size="sm" variant="outline" data-testid={`bt-att-${e.id}`} onClick={() => openAttFor(e)} title="Mark attendance"><CalendarCheck className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`bt-ot-${e.id}`} onClick={() => openOtFor(e)} title="Mark overtime"><Timer className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`bt-leave-${e.id}`} onClick={() => openLeaveFor(e)} title="Log leave"><PlaneTakeoff className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
            {data.team.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No staff assigned to this branch yet. Ask HR to assign staff from the Employees dashboard.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Attendance dialog */}
      <Dialog open={attOpen} onOpenChange={setAttOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Mark Attendance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={attForm.status} onValueChange={(v) => setAttForm({ ...attForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="present">present</SelectItem>
                    <SelectItem value="absent">absent</SelectItem>
                    <SelectItem value="half_day">half day</SelectItem>
                    <SelectItem value="leave">leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Shift</Label>
                <Select value={attForm.shift_id || "__none__"} onValueChange={(v) => setAttForm({ ...attForm, shift_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="bt-att-shift"><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">— none —</SelectItem>
                    {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div />
              <div><Label>Check-in</Label><Input type="time" value={attForm.check_in} onChange={(e) => setAttForm({ ...attForm, check_in: e.target.value })} /></div>
              <div><Label>Check-out</Label><Input type="time" value={attForm.check_out} onChange={(e) => setAttForm({ ...attForm, check_out: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAttOpen(false)}>Cancel</Button><Button data-testid="bt-att-save" onClick={saveAtt} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OT dialog */}
      <Dialog open={otOpen} onOpenChange={setOtOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Mark Overtime</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input type="date" value={otForm.date} onChange={(e) => setOtForm({ ...otForm, date: e.target.value })} /></div>
              <div><Label>Hours</Label><Input type="number" step="0.5" value={otForm.hours} onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })} /></div>
              <div><Label>Rate ×</Label><Input type="number" step="0.1" value={otForm.rate_multiplier} onChange={(e) => setOtForm({ ...otForm, rate_multiplier: e.target.value })} /></div>
            </div>
            <div><Label>Reason</Label><Input value={otForm.reason} onChange={(e) => setOtForm({ ...otForm, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOtOpen(false)}>Cancel</Button><Button data-testid="bt-ot-save" onClick={saveOt} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Log Leave Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="casual">casual</SelectItem>
                    <SelectItem value="sick">sick</SelectItem>
                    <SelectItem value="paid">paid</SelectItem>
                    <SelectItem value="unpaid">unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div />
              <div><Label>From</Label><Input type="date" value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} /></div>
              <div><Label>To</Label><Input type="date" value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} /></div>
            </div>
            <div><Label>Reason</Label><Textarea rows={2} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLeaveOpen(false)}>Cancel</Button><Button data-testid="bt-leave-save" onClick={saveLeave} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const map = {
    green: "text-emerald-700 border-emerald-200 bg-emerald-50",
    red: "text-red-700 border-red-200 bg-red-50",
    amber: "text-amber-700 border-amber-200 bg-amber-50",
    blue: "text-blue-700 border-blue-200 bg-blue-50",
    purple: "text-purple-700 border-purple-200 bg-purple-50",
    orange: "text-[#C05621] border-orange-200 bg-orange-50",
    slate: "text-stone-700 border-stone-200 bg-stone-50",
  };
  const cls = map[tone] || map.slate;
  return (
    <div className={`border rounded-md p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
